import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Filter, Activity, Clock, Smile, BookOpen, Target, Gauge } from "lucide-react";
import { useTheme, useThemeClasses } from "../hooks/useTheme";
import { useUser } from "../context/UserContext";
import { API } from "../utils/constants";

export const TeamSessionsPage = () => {
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const theme = useThemeClasses(isDark);
  const { user, isCoach } = useUser();
  
  const [sessions, setSessions] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  
  const [period, setPeriod] = useState('month');
  const [sessionType, setSessionType] = useState('');
  const [selectedDay, setSelectedDay] = useState('');
  const [sortOrder, setSortOrder] = useState('newest');
  const [showFilters, setShowFilters] = useState(false);
  
  const periodOptions = [
    { value: 'week', label: 'This Week' },
    { value: 'month', label: 'This Month' },
    { value: 'season', label: 'This Season' },
    { value: 'year', label: 'This Year' },
    { value: 'all', label: 'All Time' }
  ];
  
  const dayOptions = [
    { value: '', label: 'All Days' },
    { value: '0', label: 'Monday' },
    { value: '1', label: 'Tuesday' },
    { value: '2', label: 'Wednesday' },
    { value: '3', label: 'Thursday' },
    { value: '4', label: 'Friday' },
    { value: '5', label: 'Saturday' },
    { value: '6', label: 'Sunday' }
  ];
  
  const sessionTypes = ['Team Training', 'Specialist Training', 'Match', 'Recovery', 'Strength', 'Cardio'];

  const sortOptions = [
    { value: 'newest', label: 'Newest first' },
    { value: 'oldest', label: 'Oldest first' },
  ];

  useEffect(() => {
    const fetchSessions = async () => {
      if (!user?.id || !isCoach) return;
      setLoading(true);
      try {
        let url = `${API}/coach/team/sessions?coach_id=${user.id}&period=${period}`;
        if (sessionType) url += `&session_type=${encodeURIComponent(sessionType)}`;
        if (selectedDay) url += `&day_of_week=${selectedDay}`;
        
        const res = await axios.get(url);
        // Use grouped_sessions if available (aggregated by activity), fallback to sessions
        setSessions(res.data.grouped_sessions || res.data.sessions || []);
        setSummary(res.data.summary || null);
      } catch (e) {
        console.error("Error fetching sessions:", e);
      } finally {
        setLoading(false);
      }
    };
    fetchSessions();
  }, [user?.id, isCoach, period, sessionType, selectedDay]);

  const formatDuration = (minutes = 0) => {
    const total = Math.round(minutes || 0);
    const hours = Math.floor(total / 60);
    const mins = total % 60;
    if (hours > 0) return `${hours}h ${mins}m`;
    return `${mins}m`;
  };

  const formatDate = (value) => {
    if (!value) return "";
    const date = new Date(value);
    return date.toLocaleDateString("nl-NL");
  };

  const averageRatings = {
    fun: summary?.avg_fun || 0,
    learning: summary?.avg_learning || 0,
    mygoal: summary?.avg_mygoal || 0,
    intensity: summary?.avg_intensity || 0,
  };

  const byTypeRows = Object.entries(summary?.by_type || {})
    .map(([type, info]) => ({ type, count: info.count || 0 }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 4);

  const byAthleteRows = Object.entries(summary?.by_athlete || {})
    .map(([name, info]) => ({ name, count: info.count || 0 }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 4);

  const dayRows = [
    { key: "Monday", label: "Mon", dayOfWeek: "0" },
    { key: "Tuesday", label: "Tue", dayOfWeek: "1" },
    { key: "Wednesday", label: "Wed", dayOfWeek: "2" },
    { key: "Thursday", label: "Thu", dayOfWeek: "3" },
    { key: "Friday", label: "Fri", dayOfWeek: "4" },
    { key: "Saturday", label: "Sat", dayOfWeek: "5" },
    { key: "Sunday", label: "Sun", dayOfWeek: "6" }
  ].map((day) => ({
    ...day,
    count: summary?.by_day?.[day.key]?.count || 0,
  }));

  const maxDayCount = Math.max(1, ...dayRows.map((day) => day.count));
  const ratedSessions = sessions
    .filter((session) => session.rating)
    .slice()
    .sort((a, b) => {
      const aTime = new Date(a.completed_at || 0).getTime();
      const bTime = new Date(b.completed_at || 0).getTime();
      return sortOrder === 'oldest' ? aTime - bTime : bTime - aTime;
    });

  const getTrendMeta = (value) => {
    const pct = typeof value === "number" ? value : 0;

    if (period === "all") {
      return { text: "→ 0.0%", className: isDark ? "text-white/50" : "text-[#493986]/50" };
    }

    if (pct > 0) {
      return { text: `↑ ${Math.abs(pct).toFixed(1)}%`, className: "text-green-500" };
    }

    if (pct < 0) {
      return { text: `↓ ${Math.abs(pct).toFixed(1)}%`, className: "text-red-500" };
    }

    return { text: "→ 0.0%", className: isDark ? "text-white/50" : "text-[#493986]/50" };
  };

  const sessionsTrend = getTrendMeta(summary?.trends?.sessions_change_pct);
  const totalTimeTrend = getTrendMeta(summary?.trends?.total_duration_change_pct);
  const avgSessionTrend = getTrendMeta(summary?.trends?.average_duration_change_pct);



  return (
    <div className={`min-h-screen ${theme.bg} flex flex-col transition-colors duration-300`} data-testid="page-team-sessions">
      <header className="flex items-center justify-between p-4 pt-6">
        <button data-testid="back-button" onClick={() => navigate("/")} className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${theme.button}`}>
          <ArrowLeft className={`w-5 h-5 ${theme.textPrimary}`} />
        </button>
        <h1 className={`text-xl font-bold ${theme.textPrimary}`}>Team Sessions</h1>
        <button data-testid="filter-button" onClick={() => setShowFilters(!showFilters)} className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${showFilters ? 'bg-gradient-to-r from-[#493986] to-[#92a7e8]' : theme.button}`}>
          <Filter className={`w-5 h-5 ${showFilters ? 'text-white' : theme.textPrimary}`} />
        </button>
      </header>

      {/* Filters */}
      {showFilters && (
        <div className={`mx-4 p-4 rounded-2xl ${theme.card} border backdrop-blur-xl mb-4`}>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={`text-xs ${theme.textMuted} mb-1 block`}>Period</label>
              <select data-testid="team-sessions-period-select" value={period} onChange={(e) => setPeriod(e.target.value)} className={`w-full px-3 py-2 rounded-xl border text-sm ${theme.input}`} style={{ color: isDark ? '#fff' : '#1a1a2e' }}>
                {periodOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
              </select>
            </div>
            <div>
              <label className={`text-xs ${theme.textMuted} mb-1 block`}>Day</label>
              <select data-testid="team-sessions-day-select" value={selectedDay} onChange={(e) => setSelectedDay(e.target.value)} className={`w-full px-3 py-2 rounded-xl border text-sm ${theme.input}`} style={{ color: isDark ? '#fff' : '#1a1a2e' }}>
                {dayOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
              </select>
            </div>
            <div>
              <label className={`text-xs ${theme.textMuted} mb-1 block`}>Type</label>
              <select data-testid="team-sessions-type-select" value={sessionType} onChange={(e) => setSessionType(e.target.value)} className={`w-full px-3 py-2 rounded-xl border text-sm ${theme.input}`} style={{ color: isDark ? '#fff' : '#1a1a2e' }}>
                <option value="">All Types</option>
                {sessionTypes.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <p className={`text-xs self-center ${theme.textMuted}`}>Rating scores are shown for the team. Individual player scores stay private.</p>
            <div className="col-span-2">
              <label className={`text-xs ${theme.textMuted} mb-1 block`}>Sort by date</label>
              <select data-testid="team-sessions-sort-select" value={sortOrder} onChange={(e) => setSortOrder(e.target.value)} className={`w-full px-3 py-2 rounded-xl border text-sm ${theme.input}`} style={{ color: isDark ? '#fff' : '#1a1a2e' }}>
                {sortOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
              </select>
            </div>
          </div>
        </div>
      )}

      <div className="flex-1 px-4 pb-8 overflow-y-auto">
        {loading ? (
          <div className={`text-center py-8 ${theme.textMuted}`}>Loading...</div>
        ) : (
          <>
            {summary && (
              <div className={`p-4 rounded-3xl ${theme.card} border backdrop-blur-xl mb-4`} data-testid="team-sessions-summary-card">
                <h2 className={`text-2xl font-semibold mb-4 ${theme.textPrimary}`}>Summary</h2>

                <div className="grid grid-cols-3 gap-3 text-center mb-4">
                  <div data-testid="team-sessions-total-sessions">
                    <p className={`text-3xl font-bold ${theme.textPrimary}`}>{summary.total_sessions || 0}</p>
                    <p className={`text-sm ${theme.textMuted}`}>Sessions</p>
                    <p data-testid="team-sessions-trend-sessions" className={`text-xs font-semibold ${sessionsTrend.className}`}>{sessionsTrend.text}</p>
                  </div>
                  <div data-testid="team-sessions-total-time">
                    <p className={`text-3xl font-bold ${theme.textPrimary}`}>{formatDuration(summary.total_duration)}</p>
                    <p className={`text-sm ${theme.textMuted}`}>Total Time</p>
                    <p data-testid="team-sessions-trend-total-time" className={`text-xs font-semibold ${totalTimeTrend.className}`}>{totalTimeTrend.text}</p>
                  </div>
                  <div data-testid="team-sessions-avg-session">
                    <p className={`text-3xl font-bold ${theme.textPrimary}`}>{formatDuration(summary.average_duration)}</p>
                    <p className={`text-sm ${theme.textMuted}`}>Avg/Session</p>
                    <p data-testid="team-sessions-trend-avg-session" className={`text-xs font-semibold ${avgSessionTrend.className}`}>{avgSessionTrend.text}</p>
                  </div>
                </div>

                <div className={`h-px ${isDark ? 'bg-white/10' : 'bg-[#493986]/10'} mb-4`} />

                <h3 className={`text-xl font-semibold mb-3 ${theme.textPrimary}`}>
                  Average Ratings ({summary.rated_sessions || 0} rated)
                </h3>
                <div className="grid grid-cols-4 gap-2">
                  <div className={`rounded-2xl p-2 text-center ${isDark ? 'bg-green-500/20' : 'bg-green-100'}`}>
                    <Smile className="w-4 h-4 mx-auto mb-1 text-green-600" />
                    <p className="text-2xl font-bold text-green-600">{averageRatings.fun.toFixed(1)}</p>
                    <p className={`text-xs ${theme.textMuted}`}>Fun</p>
                  </div>
                  <div className={`rounded-2xl p-2 text-center ${isDark ? 'bg-blue-500/20' : 'bg-blue-100'}`}>
                    <BookOpen className="w-4 h-4 mx-auto mb-1 text-blue-600" />
                    <p className="text-2xl font-bold text-blue-600">{averageRatings.learning.toFixed(1)}</p>
                    <p className={`text-xs ${theme.textMuted}`}>Learn</p>
                  </div>
                  <div className={`rounded-2xl p-2 text-center ${isDark ? 'bg-purple-500/20' : 'bg-purple-100'}`}>
                    <Target className="w-4 h-4 mx-auto mb-1 text-purple-600" />
                    <p className="text-2xl font-bold text-purple-600">{averageRatings.mygoal.toFixed(1)}</p>
                    <p className={`text-xs ${theme.textMuted}`}>Goal</p>
                  </div>
                  <div className={`rounded-2xl p-2 text-center ${isDark ? 'bg-orange-500/20' : 'bg-orange-100'}`}>
                    <Gauge className="w-4 h-4 mx-auto mb-1 text-orange-600" />
                    <p className="text-2xl font-bold text-orange-600">{averageRatings.intensity.toFixed(1)}</p>
                    <p className={`text-xs ${theme.textMuted}`}>Intensity</p>
                  </div>
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3 mb-4">
              <div className={`p-4 rounded-3xl ${theme.card} border backdrop-blur-xl`} data-testid="team-sessions-by-type-card">
                <h3 className={`text-2xl font-semibold mb-3 ${theme.textPrimary}`}>By Type</h3>
                <div className="space-y-2">
                  {byTypeRows.length === 0 ? (
                    <p className={`text-sm ${theme.textMuted}`}>No type data</p>
                  ) : (
                    byTypeRows.map((row) => (
                      <div key={row.type} className="flex items-center justify-between gap-2" data-testid={`team-sessions-type-${row.type.replace(/\s+/g, '-').toLowerCase()}`}>
                        <p className={`text-sm ${theme.textSecondary}`}>{row.type}</p>
                        <p className={`text-2xl font-semibold ${theme.textPrimary}`}>{row.count}</p>
                      </div>
                    ))
                  )}
                </div>
              </div>

              <div className={`p-4 rounded-3xl ${theme.card} border backdrop-blur-xl`} data-testid="team-sessions-by-athlete-card">
                <h3 className={`text-2xl font-semibold mb-3 ${theme.textPrimary}`}>By Athlete</h3>
                <div className="space-y-2">
                  {byAthleteRows.length === 0 ? (
                    <p className={`text-sm ${theme.textMuted}`}>No athlete data</p>
                  ) : (
                    byAthleteRows.map((row) => (
                      <div key={row.name} className="flex items-center justify-between gap-2" data-testid={`team-sessions-athlete-${row.name.replace(/\s+/g, '-').toLowerCase()}`}>
                        <p className={`text-sm truncate ${theme.textSecondary}`}>{row.name}</p>
                        <p className={`text-2xl font-semibold ${theme.textPrimary}`}>{row.count}</p>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>

            <div className={`p-4 rounded-3xl ${theme.card} border backdrop-blur-xl mb-4`} data-testid="team-sessions-by-day-card">
              <div className="flex items-center justify-between mb-4">
                <h3 className={`text-2xl font-semibold ${theme.textPrimary}`}>Sessions by Day</h3>
                {selectedDay && (
                  <button
                    type="button"
                    onClick={() => setSelectedDay('')}
                    className={`text-xs font-medium px-3 py-1 rounded-full ${isDark ? 'bg-white/10 hover:bg-white/15 text-white/70' : 'bg-[#493986]/10 hover:bg-[#493986]/20 text-[#493986]'}`}
                    data-testid="team-sessions-clear-day-filter"
                  >
                    Clear filter ✕
                  </button>
                )}
              </div>
              <div className="grid grid-cols-7 gap-2 items-end">
                {dayRows.map((day) => {
                  const isActive = selectedDay === day.dayOfWeek;
                  return (
                    <button
                      type="button"
                      key={day.key}
                      onClick={() => setSelectedDay(isActive ? '' : day.dayOfWeek)}
                      className={`text-center rounded-2xl py-2 transition-all ${isActive ? (isDark ? 'bg-white/10 ring-2 ring-[#92a7e8]' : 'bg-[#493986]/10 ring-2 ring-[#493986]') : 'hover:bg-white/5'}`}
                      data-testid={`team-sessions-day-${day.label.toLowerCase()}`}
                      aria-pressed={isActive}
                    >
                      <div
                        className={`mx-auto w-full rounded-t-3xl mb-2 transition-colors ${
                          isActive
                            ? 'bg-gradient-to-t from-[#493986] to-[#92a7e8]'
                            : isDark
                              ? 'bg-[#c4b0f5]/70 hover:bg-[#c4b0f5]'
                              : 'bg-[#d9caf8] hover:bg-[#c4b0f5]'
                        }`}
                        style={{ height: `${32 + (day.count / maxDayCount) * 46}px` }}
                      />
                      <p className={`text-sm ${theme.textSecondary}`}>{day.label}</p>
                      <p className={`text-2xl font-semibold ${theme.textPrimary}`}>{day.count}</p>
                    </button>
                  );
                })}
              </div>
            </div>

            <h3 className={`text-4xl font-semibold mb-3 ${theme.textPrimary}`} data-testid="team-sessions-rated-title">
              Rated Sessions ({summary?.rated_sessions || 0})
            </h3>

            {ratedSessions.length > 0 ? (
              <div className="space-y-3" data-testid="team-sessions-rated-list">
                {ratedSessions.slice(0, 12).map((session, idx) => {
                  const historicalAvg = {
                    fun: summary?.avg_fun || 0,
                    learning: summary?.avg_learning || 0,
                    mygoal: summary?.avg_mygoal || 0,
                    intensity: summary?.avg_intensity || 0,
                  };
                  const ratingTopics = [
                    { key: "fun", label: "Fun", icon: Smile, color: "text-yellow-400" },
                    { key: "learning", label: "Learning", icon: BookOpen, color: "text-blue-400" },
                    { key: "mygoal", label: "MyGoal", icon: Target, color: "text-purple-400" },
                    { key: "intensity", label: "Intensity", icon: Gauge, color: "text-red-400" },
                  ];
                  const avgRating = session.rating || {};
                  const minRating = session.min_rating || avgRating;
                  const maxRating = session.max_rating || avgRating;
                  const overallScore = (ratingTopics.reduce((sum, t) => sum + (avgRating[t.key] || 0), 0) / ratingTopics.length);
                  const overallIndicator = overallScore >= 7.5
                    ? { arrow: "↑", className: "text-green-500" }
                    : overallScore >= 5.5
                      ? { arrow: "→", className: isDark ? "text-white/60" : "text-[#493986]/60" }
                      : { arrow: "↓", className: "text-red-500" };

                  const ratingCount = session.rating_count || (session.rating ? 1 : 0);
                  const totalAthletes = session.total_athletes || ratingCount;

                  return (
                    <div key={`${session.id || session.name}-${idx}`} className={`p-4 rounded-3xl ${theme.card} border backdrop-blur-xl`} data-testid={`team-sessions-rated-item-${idx}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <p className={`text-2xl font-semibold ${theme.textPrimary}`}>{session.name}</p>
                        </div>
                        <div
                          data-testid={`team-sessions-overall-badge-${idx}`}
                          className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full border ${isDark ? 'bg-white/10 border-white/15' : 'bg-[#493986]/8 border-[#493986]/20'}`}
                        >
                          <span className={`text-sm font-bold ${overallIndicator.className}`}>{overallIndicator.arrow}</span>
                          <span className={`text-xs font-semibold ${theme.textPrimary}`}>⭐ {overallScore.toFixed(1)}</span>
                        </div>
                      </div>
                      <div className={`mt-2 flex items-center gap-3 text-sm ${theme.textMuted}`}>
                        <span className="inline-flex items-center gap-1"><Clock className="w-4 h-4" />{formatDuration(session.duration)}</span>
                        <span>{formatDate(session.completed_at)}</span>
                        <span className="inline-flex items-center gap-1" data-testid={`team-sessions-filled-${idx}`}>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${isDark ? 'bg-white/10 text-white/80' : 'bg-[#493986]/10 text-[#493986]'}`}>
                            {ratingCount}/{totalAthletes} filled in
                          </span>
                        </span>
                      </div>

                      <div className="mt-4 space-y-4" data-testid={`team-sessions-rated-graph-${idx}`}>
                        {ratingTopics.map(topic => {
                          const avg = avgRating[topic.key] || 0;
                          const min = minRating[topic.key] || 0;
                          const max = maxRating[topic.key] || 0;
                          const hist = historicalAvg[topic.key] || 0;
                          const Icon = topic.icon;
                          const delta = hist > 0 ? avg - hist : 0;
                          const deltaArrow = delta > 0 ? '↑' : delta < 0 ? '↓' : '=';
                          const deltaColor = delta > 0 ? 'text-green-500' : delta < 0 ? 'text-red-500' : theme.textMuted;
                          return (
                            <div key={`${session.id || idx}-${topic.key}`} className={`p-3 rounded-xl ${isDark ? 'bg-white/5' : 'bg-gray-50'}`} data-testid={`team-sessions-rated-graph-row-${idx}-${topic.key}`}>
                              <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center gap-2">
                                  <Icon className={`w-4 h-4 ${topic.color}`} />
                                  <span className={`text-sm font-medium ${theme.textPrimary}`}>{topic.label}</span>
                                </div>
                                <div className="flex items-center gap-2 text-xs">
                                  <span className={`px-2 py-0.5 rounded ${isDark ? 'bg-red-500/20 text-red-400' : 'bg-red-100 text-red-600'}`}>{min}</span>
                                  <span className={`px-2 py-0.5 rounded font-bold ${isDark ? 'bg-purple-500/20 text-purple-400' : 'bg-purple-100 text-purple-600'}`}>{avg.toFixed ? avg.toFixed(1) : avg}</span>
                                  <span className={`px-2 py-0.5 rounded ${isDark ? 'bg-green-500/20 text-green-400' : 'bg-green-100 text-green-600'}`}>{max}</span>
                                </div>
                              </div>
                              {/* Bandwidth bar */}
                              <div className={`relative h-4 rounded-full ${isDark ? 'bg-white/10' : 'bg-gray-200'} overflow-hidden`}>
                                <div
                                  className={`absolute h-full ${isDark ? 'bg-gradient-to-r from-red-500/40 via-purple-500/40 to-green-500/40' : 'bg-gradient-to-r from-red-200 via-purple-200 to-green-200'}`}
                                  style={{ left: `${(min / 10) * 100}%`, width: `${Math.max(0, (max - min) / 10 * 100)}%` }}
                                />
                                <div className="absolute top-0 bottom-0 w-1 bg-red-500 rounded" style={{ left: `${(min / 10) * 100}%` }} />
                                <div className="absolute top-0 bottom-0 w-1.5 bg-[#493986] rounded z-10" style={{ left: `${(avg / 10) * 100}%` }} />
                                <div className="absolute top-0 bottom-0 w-1 bg-green-500 rounded" style={{ left: `${(max / 10) * 100}%` }} />
                              </div>
                              {hist > 0 && (
                                <div className="flex items-center justify-between mt-1">
                                  <span className={`text-xs ${theme.textMuted}`}>Historical avg: {hist.toFixed ? hist.toFixed(1) : hist}</span>
                                  <span className={`text-xs ${deltaColor}`}>
                                    {deltaArrow} {Math.abs(delta).toFixed(1)} vs history
                                  </span>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>

                      {/* Legend */}
                      <div className={`mt-3 pt-2 border-t ${isDark ? 'border-white/10' : 'border-gray-200'} flex items-center justify-center gap-4 text-xs ${theme.textMuted}`}>
                        <span className="flex items-center gap-1"><div className="w-2 h-2 rounded bg-red-500" /> Min</span>
                        <span className="flex items-center gap-1"><div className="w-2 h-2 rounded bg-[#493986]" /> Avg</span>
                        <span className="flex items-center gap-1"><div className="w-2 h-2 rounded bg-green-500" /> Max</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className={`text-center py-10`}>
                <Activity className={`w-14 h-14 mx-auto mb-3 ${theme.textMuted}`} />
                <p className={`text-sm ${theme.textMuted}`}>No rated sessions found for this filter.</p>
              </div>
            )}
          </>
        )}
      </div>

    </div>
  );
};

export default TeamSessionsPage;
